import RecordView from "@/components/record/RecordView";
import { Clock2Icon, PercentIcon } from "lucide-react";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "@tanstack/react-query";
import { ReadProjectSettlements } from "@/schemas/projectSettlements";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { getRenderer } from "@/modules/projects/components/settlementsDetails/utils/getRenderer";

interface ResultDataSectionComponentProps {
  query: UseQueryResult<ReadProjectSettlements>;
}

const ResultDataSectionComponent = ({
  query,
}: ResultDataSectionComponentProps) => {
  const { i18n } = useTranslation();

  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  const rendererWithIcon = useMemo(
    () => getRenderer(numberFormatter),
    [numberFormatter],
  );

  return (
    <RecordView
      isOpen={true}
      query={query}
      translationKey={"projectSettlements"}
      header={() => i18n.t("projectSettlements.sections.resultData.header")}
    >
      <RecordView.Field
        name="invoiced_hours"
        renderer={rendererWithIcon(<Clock2Icon className="w-4 h-4" />)}
      />
      <RecordView.Field name="invoiced_avg_rate" renderer={numberFormatter} />
      <RecordView.Field name="invoiced_income" renderer={numberFormatter} />
      <RecordView.Field
        name="contractors_hours"
        renderer={rendererWithIcon(<Clock2Icon className="w-4 h-4" />)}
      />
      <RecordView.Field
        name="contractors_avg_rate"
        renderer={numberFormatter}
      />
      <RecordView.Field name="contractors_payment" renderer={numberFormatter} />
      <RecordView.Field
        name="profitability"
        renderer={rendererWithIcon(<PercentIcon className="w-4 h-4" />)}
      />
    </RecordView>
  );
};

export const ResultDataSection = memo(
  ResultDataSectionComponent,
  (prevProps, nextProps) => {
    return prevProps.query.data?.id === nextProps.query.data?.id;
  },
);
